<template>
    <div class="checkbox-group">
        <div
            v-for="(item, index) in options"
            :key="getItemKey(item)"
            :class="[
                {
                    'is-checked': isChecked(item),
                    'is-disabled': isItemDisabled(item)
                },
                itemClass
            ]"
            class="checkbox-group-item-wrap"
            :style="itemStyle"
        >
            <div
                class="checkbox-group-item"
                @click="handleItemClick(item)"
            >
                <!-- 自定义插槽 -->
                <slot
                    :item="item"
                    :index="index"
                    :checked="isChecked(item)"
                    :disabled="isItemDisabled(item)"
                >
                    <!-- 默认渲染 -->
                    <div class="checkbox-item-default">
                        <span
                            class="checkbox-icon"
                            :class="{ 'is-checked': isChecked(item) }"
                        >
                            <span v-if="isChecked(item)" class="checkbox-inner"></span>
                        </span>
                        <span class="checkbox-label">{{ getItemLabel(item) }}</span>
                    </div>
                </slot>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'CheckboxGroup',
    props: {
        value: {
            type: [Array, String, Number, Boolean],
            default: () => []
        },
        // 选项数据
        options: {
            type: Array,
            required: true,
            default: () => []
        },
        /** 选择模式: 'single' 单选, 'multiple' 多选 */
        mode: {
            type: String,
            default: 'single'
        },
        // 禁用
        disabled: {
            type: Boolean,
            default: false
        },
        // 选项的 key 字段名
        valueKey: {
            type: String,
            default: 'value'
        },
        // 选项的 label 字段名
        labelKey: {
            type: String,
            default: 'label'
        },
        // 选项的 disabled 字段名
        disabledKey: {
            type: String,
            default: 'disabled'
        },
        // 自定义 item 样式类名
        itemClass: {
            type: [String, Object, Array],
            default: ''
        },
        // 自定义 item 内联样式
        itemStyle: {
            type: [String, Object],
            default: ''
        }
    },
    computed: {
        single() {

            return this.mode === 'single';
        
        },
        // 当前选中的值映射
        checkedValues() {

            if ( this.single ) {

                return this.value !== null && this.value !== undefined ? [this.value] : [];
            
            }
            return Array.isArray( this.value ) ? this.value : [];
        
        }
    },
    methods: {
    // 获取选项的唯一标识
        getItemKey( item ) {

            if ( typeof item === 'object' && item !== null ) {

                return item[this.valueKey];
            
            }
            return item;
        
        },
    
        // 获取选项的值
        getItemValue( item ) {

            if ( typeof item === 'object' && item !== null ) {

                return item[this.valueKey];
            
            }
            return item;
        
        },
    
        // 获取选项的标签
        getItemLabel( item ) {

            if ( typeof item === 'object' && item !== null ) {

                return item[this.labelKey] !== undefined ? item[this.labelKey] : item[this.valueKey];
            
            }
            return item;
        
        },
    
        // 判断选项是否被禁用
        isItemDisabled( item ) {

            if ( this.disabled ) {

                return true;
            
            }
            if ( typeof item === 'object' && item !== null ) {

                return !!item[this.disabledKey];
            
            }
            return false;
        
        },
    
        isChecked( item ) {

            const itemValue = this.getItemValue( item );

            return this.checkedValues.some( val => {

                return val === itemValue;
            
            });
        
        },
    
        handleItemClick( item ) {

            if ( this.isItemDisabled( item )) {

                return;
            
            }
      
            const itemValue = this.getItemValue( item );

            const isChecked = this.isChecked( item );
      
            if ( this.single ) {

                // 单选且已选中则不做任何操作
                if ( isChecked ) return;

                // 单选模式
                const newValue = isChecked ? null : itemValue;
                this.$emit( 'input', newValue );
                this.$emit( 'change', newValue, item );
            
            } else {

                // 多选模式
                let newValues = [...this.checkedValues];

                if ( isChecked ) {

                    newValues = newValues.filter( val => val !== itemValue );
                
                } else {

                    newValues.push( itemValue );
                
                }
        
                this.$emit( 'input', newValues );
                this.$emit( 'change', newValues, item );
            
            }
        
        }
    }
};
</script>

<style lang="less" scoped>
.checkbox-group {
  display: flex;
  gap: 0px;
  flex-direction: column;
}

.checkbox-group-item-wrap {
    padding: 12px 0px;
    border-bottom: 1px solid #E1E1E1;
}

.checkbox-group-item {
    cursor: pointer;
    transition: all 0.3s;
    user-select: none;
    display: inline-block;
    
    &.is-disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
  
    &:not(.is-disabled):hover {
        opacity: 0.8;
    }
}

.checkbox-item-default {
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: #fff;
    transition: all 0.3s;
}

.checkbox-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 1px solid #dcdfe6;
  border-radius: 50%;
  background-color: #fff;
  transition: all 0.3s;
  
  &.is-checked {
    border-color: #0050E2;
    background-color: #0050E2;
  }
}

.checkbox-inner {
  display: block;
  width: 4px;
  height: 8px;
  border: 2px solid #fff;
  border-top: 0;
  border-left: 0;
  transform: rotate(45deg) translateY(-1px);
}

.checkbox-label {
  color: #333333;
  font-size: 16px;
}
</style>
